import {useTranslation} from '@/translations/i18n.tsx'
import {useURLParsedParams} from '@hooks/useURLParsedParams'
import {useAssignment} from '@features/assignment/queries/useAssignment'
import {Skeleton} from '@components/ui/skeleton'
import {ErrorBox} from '@components/commons/error-box'
import {ButtonLink} from '@components/commons/button-link'
import {AxiosError} from 'axios'
import {AssignmentUrlParams} from '@features/assignment/types.ts'
import {AlertTriangleIcon, Globe01Icon, Mail01Icon, PhoneIcon} from '@components/ui/icon'
import {Avatar} from '@components/ui/avatar'
import {ASSIGNMENT_CARD_WRAPPER_CLASSNAME, ASSIGNMENT_CARD_TITLE_CLASSNAME} from '@features/assignment/constants'

export const AssignmentHostCard = () => {
    const {t} = useTranslation()
    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const assignmentQuery = useAssignment(urlParams.success ? urlParams.data.code : undefined, {
        enabled: urlParams.success
    })

    if (assignmentQuery.isPending) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>
                    <Skeleton height={28} />
                </h3>
                <div className="flex flex-col gap-2 w-full">
                    {Array.from({length: 3}).map((_, index) => (
                        <Skeleton key={index} height={20} />
                    ))}
                </div>
            </div>
        )
    }

    if (
        !urlParams.success ||
        (assignmentQuery.error instanceof AxiosError && assignmentQuery.error.response?.status == 404) ||
        (assignmentQuery.data?.status != 'pending' && assignmentQuery.data?.status != 'accepted')
    ) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('spaces:not_found')} />
            </div>
        )
    }

    return (
        <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
            <div className="flex justify-between w-full gap-4">
                <div className="flex flex-col gap-4">
                    <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>
                        {t('commons:host')}: {assignmentQuery.data.host.first_name}
                    </h3>

                    {assignmentQuery.data?.message_thread_url && (
                        <ButtonLink
                            className="max-w-[230px] w-full"
                            to={assignmentQuery.data.message_thread_url}
                            target="_blank"
                        >
                            {t('assignment:message_host')}
                        </ButtonLink>
                    )}

                    <ul className="flex flex-col gap-2 text-sm [&_a]:cursor-pointer [&_a]:underline">
                        <li className="flex items-center gap-2">
                            <Mail01Icon size={20} />
                            <span>{t('commons:email')}:</span>
                            <a href={`mailto:${assignmentQuery.data.host.email}`}>{assignmentQuery.data.host.email}</a>
                        </li>
                        {assignmentQuery.data?.host.phone && (
                            <li className="flex items-center gap-2">
                                <PhoneIcon size={20} />
                                <span>{t('assignment:phone')}:</span>
                                <a href={`tel:${assignmentQuery.data.host.phone}`}>{assignmentQuery.data.host.phone}</a>
                            </li>
                        )}
                        {assignmentQuery.data?.host.preferred_languages && (
                            <li className="flex items-center gap-2">
                                <Globe01Icon size={20} />
                                <span>{t('commons:preferred_languages')}:</span>
                                {assignmentQuery.data.host.preferred_languages}
                            </li>
                        )}
                    </ul>
                </div>
                <Avatar name={assignmentQuery.data.host.first_name} imageUrl={''} className="shrink-0" />
            </div>
        </div>
    )
}
